import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import twilioService from '../../lib/twilio-service';
import { isMasqueradeAdminEmail, readMasqueradeSession } from '../../lib/masquerade';

const Header = () => {
  const { userProfile, user } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  
  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const a2pApproved = userProfile?.a2p_status === 'approved' || userProfile?.campaign_status === 'VERIFIED';

  // Get user display name
  const displayName = userProfile?.full_name || user?.email?.split('@')[0] || 'User';

  const navigationItems = [
    { label: 'Dashboard', path: '/analytics-dashboard', icon: 'BarChart3' },
    { label: 'Clients', path: '/client-management', icon: 'Users' },
    { label: 'Messages', path: '/conversations', icon: 'MessageSquare', badge: notifications?.filter(n => n?.type === 'message' && n?.unread)?.length },
    // { label: 'Campaigns', path: '/campaign-automation', icon: 'Zap', comingSoon: true },
    { label: 'Touchpoints', path: '/evergreen-touchpoints', icon: 'Calendar' },
  ];

  const unreadCount = notifications?.filter(n => n?.unread)?.length;

  // Load real notifications from conversations
  useEffect(() => {
    if (!user?.id) return;

    const loadNotifications = async () => {
      try {
        const { data, error } = await twilioService.getConversations(user.id);
        
        if (error) {
          console.error('Error loading notifications:', error);
          return;
        }

        // Format conversations with unread messages as notifications
        const formattedNotifications = data
          ?.filter(conv => conv.unread_count > 0)
          ?.map(conv => {
            const timeAgo = (timestamp) => {
              if (!timestamp) return 'Recently';
              const now = new Date();
              const msgTime = new Date(timestamp);
              const diffInMinutes = Math.floor((now - msgTime) / (1000 * 60));
              
              if (diffInMinutes < 1) return 'Just now';
              if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
              if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
              return `${Math.floor(diffInMinutes / 1440)}d ago`;
            };

            return {
              id: conv.id,
              type: 'message',
              title: `New message from ${conv.client?.name || 'Client'}`,
              time: timeAgo(conv.last_message_at),
              unread: true,
              conversationId: conv.id
            };
          }) || [];

        setNotifications(formattedNotifications);
      } catch (err) {
        console.error('Error loading notifications:', err);
      }
    };

    loadNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
      if (notificationRef?.current && !notificationRef?.current?.contains(event?.target)) {
        setIsNotificationOpen(false);
      }
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setIsSearchOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if ((event?.ctrlKey || event?.metaKey) && event?.key === 'k') {
        event?.preventDefault();
        setIsSearchOpen(true);
      }
      if (event?.key === 'Escape') {
        setIsSearchOpen(false);
        setIsUserMenuOpen(false);
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      console.log('Searching for:', searchQuery);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleNotificationClick = (notification) => {
    setIsNotificationOpen(false);
    if (notification?.conversationId) {
      navigate('/conversations', { 
        state: { 
          conversationId: notification.conversationId
        }
      });
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 border-b border-border elevation-2 backdrop-blur-sm">
        <div className="flex items-center justify-between h-16 px-6">
          {/* Logo */}
          <div className="flex items-center">
            <div className="flex items-center space-x-2">
              <img src="/logo-icon.png" alt="Nurture Buddy.ai" className="w-9 h-9 rounded-xl object-cover" />
              <span className="font-heading text-xl font-semibold text-gradient">Nurture Buddy.ai</span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems?.map((item) => {
              const isActive = location?.pathname === item?.path;
              const isComingSoon = item?.comingSoon;
              return (
                <button
                  key={item?.path}
                  onClick={() => !isComingSoon && handleNavigation(item?.path)}
                  disabled={isComingSoon}
                  className={`relative flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth touch-target ${
                    isComingSoon
                      ? 'opacity-50 cursor-not-allowed text-muted-foreground/50'
                      : isActive
                      ? 'nav-active'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                  {isComingSoon && (
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                      Coming Soon
                    </span>
                  )}
                  {item?.badge > 0 && !isComingSoon && (
                    <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {item?.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-2">
            {/* AI Compliance indicator when not approved */}
            {user?.id && userProfile && !a2pApproved && (
              <button
                onClick={() => navigate('/settings?tab=ai-compliance')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-destructive/15 text-destructive hover:bg-destructive/25 transition-colors border border-destructive/30"
                title="Complete AI Compliance to send messages"
              >
                <Icon name="Shield" size={18} />
                <span className="text-sm font-medium hidden sm:inline">AI Compliance required</span>
                <span className="flex h-2 w-2 rounded-full bg-destructive shrink-0" aria-hidden />
              </button>
            )}

            {/* Search */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSearchOpen(true)}
              className="hidden md:flex"
            >
              <Icon name="Search" size={20} />
            </Button>

            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                className="relative"
              >
                <Icon name="Bell" size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>

              {isNotificationOpen && (
                <div className="absolute right-0 top-12 w-80 bg-popover border border-border rounded-lg elevation-3 animate-slide-in">
                  <div className="p-4 border-b border-border">
                    <h3 className="font-medium text-foreground">Notifications</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications?.map((notification) => (
                      <button
                        key={notification?.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`w-full p-4 text-left hover:bg-muted transition-smooth border-b border-border last:border-b-0 ${
                          notification?.unread ? 'bg-accent/5' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 rounded-full mt-2 ${notification?.unread ? 'bg-primary' : 'bg-muted'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {notification?.title}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {notification?.time}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="p-3 border-t border-border">
                    <Button variant="ghost" size="sm" className="w-full">
                      View all notifications
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <Button
                variant="ghost"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 px-3"
              >
                <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                <span className="hidden md:block text-sm font-medium text-foreground">
                  {displayName}
                </span>
                <Icon name="ChevronDown" size={16} />
              </Button>

              {isUserMenuOpen && (
                <div className="absolute right-0 top-12 w-56 bg-popover border border-border rounded-lg elevation-3 animate-slide-in">
                  <div className="p-3 border-b border-border">
                    <p className="text-sm font-medium text-foreground">{displayName}</p>
                    <p className="text-xs text-muted-foreground">{userProfile?.email || user?.email || ''}</p>
                  </div>
                  <div className="py-2">
                    <button 
                      onClick={() => {
                        handleNavigation('/settings');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted transition-smooth flex items-center space-x-2"
                    >
                      <Icon name="User" size={16} />
                      <span>Profile Settings</span>
                    </button>
                    <button 
                      onClick={() => {
                        handleNavigation('/settings');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted transition-smooth flex items-center space-x-2"
                    >
                      <Icon name="Settings" size={16} />
                      <span>Location & Schedule</span>
                    </button>
                    <button className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted transition-smooth flex items-center space-x-2">
                      <Icon name="HelpCircle" size={16} />
                      <span>Help & Support</span>
                    </button>
                    {isMasqueradeAdminEmail(user?.email) && !readMasqueradeSession() && (
                      <button
                        onClick={() => {
                          handleNavigation('/admin/masquerade');
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted transition-smooth flex items-center space-x-2"
                      >
                        <Icon name="Eye" size={16} />
                        <span>Masquerade</span>
                      </button>
                    )}
                  </div>
                  <div className="border-t border-border py-2">
                    <button
                      onClick={handleLogout}
                      className="w-full px-3 py-2 text-left text-sm text-error hover:bg-muted transition-smooth flex items-center space-x-2"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </header>

      {/* Mobile Bottom Navigation — fixed at bottom so pt-16 on main stays correct */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-t border-border elevation-2">
        <div className="flex items-center justify-around py-2 safe-bottom">
          {navigationItems?.map((item) => {
            const isActive = location?.pathname === item?.path;
            const isComingSoon = item?.comingSoon;
            return (
              <button
                key={item?.path}
                onClick={() => !isComingSoon && handleNavigation(item?.path)}
                disabled={isComingSoon}
                className={`relative flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-smooth touch-target ${
                  isComingSoon
                    ? 'opacity-50 cursor-not-allowed text-muted-foreground/50'
                    : isActive
                    ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                <Icon name={item?.icon} size={20} />
                <span className="text-xs font-medium">{item?.label}</span>
                {isComingSoon && (
                  <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full mt-0.5">
                    Soon
                  </span>
                )}
                {item?.badge > 0 && !isComingSoon && (
                  <span className="absolute -top-1 right-1 bg-error text-error-foreground text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {item?.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
      {/* Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm animate-fade-in">
          <div className="flex items-start justify-center pt-20 px-4">
            <div ref={searchRef} className="w-full max-w-2xl bg-card border border-border rounded-lg elevation-4 animate-scale-in">
              <form onSubmit={handleSearch} className="p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Search" size={20} className="text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search clients, conversations, campaigns..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="flex-1 bg-transparent border-none outline-none text-foreground placeholder-muted-foreground"
                    autoFocus
                  />
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <kbd className="px-2 py-1 bg-muted rounded border">Ctrl</kbd>
                    <span>+</span>
                    <kbd className="px-2 py-1 bg-muted rounded border">K</kbd>
                  </div>
                </div>
              </form>
              <div className="border-t border-border p-4">
                <p className="text-sm text-muted-foreground">
                  Start typing to search across clients, conversations, and campaigns
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;